import React, { useCallback, useState } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '../utils/helpers';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  isLoading?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, isLoading = false }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
    ];
    
    const allowedExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      return 'Please upload a valid Excel file (.xlsx, .xls) or CSV file (.csv)';
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      return 'File size must be less than 10MB';
    }
    
    return null;
  };

  const handleFileSelect = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }
    
    setError(null);
    setSelectedFile(file);
    onFileSelect(file);
  }, [onFileSelect]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div
        className={cn(
          "relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200",
          isDragOver
            ? "border-primary-400 bg-primary-50"
            : "border-secondary-300 hover:border-secondary-400",
          error && "border-accent-400 bg-accent-50",
          selectedFile && !error && "border-success-400 bg-success-50"
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileInputChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isLoading}
        />
        
        <div className="space-y-4">
          <div className="flex justify-center">
            {error ? (
              <AlertCircle className="w-12 h-12 text-accent-500" />
            ) : selectedFile && !error ? (
              <CheckCircle className="w-12 h-12 text-success-500" />
            ) : (
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <Upload className="w-6 h-6 text-primary-600" />
              </div>
            )}
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">
              {error ? 'Upload Error' : selectedFile ? 'File Selected' : 'Upload Member Data'}
            </h3>
            <p className="text-secondary-600">
              {error 
                ? error
                : selectedFile 
                  ? `Selected: ${selectedFile.name}`
                  : 'Drag and drop your Excel file here, or click to browse'
              }
            </p>
          </div>
          
          {!selectedFile && !error && (
            <div className="flex items-center justify-center space-x-2 text-sm text-secondary-500">
              <FileSpreadsheet className="w-4 h-4" />
              <span>Supports .xlsx, .xls, and .csv files</span>
            </div>
          )}
          
          {selectedFile && !error && (
            <div className="flex items-center justify-center space-x-2 text-sm text-success-600">
              <CheckCircle className="w-4 h-4" />
              <span>Ready to process</span>
            </div>
          )}
        </div>
      </div>
      
      {selectedFile && !error && (
        <div className="mt-4 p-4 bg-secondary-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileSpreadsheet className="w-5 h-5 text-secondary-500" />
              <div>
                <p className="font-medium text-secondary-900">{selectedFile.name}</p>
                <p className="text-sm text-secondary-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedFile(null);
                setError(null);
              }}
              className="text-secondary-400 hover:text-secondary-600 transition-colors duration-200"
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
